import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CardComp } from "../../../components";
import { ButtonComp, ModalCompAdvance } from "../../../components/Ui";
import {
  AuditEntryDetailModal,
  getAuditRowSummary,
} from "../../../components/Admin/AuditLog/AuditEntryDetailModal";
import {
  useGetAuditLogsQuery,
  useGetAuditLogActionsQuery,
} from "../../../store/admin/settings/auditLogSlice";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import EmptyTable from "../../../components/Common/EmptyTable";
import { TableCompData } from "../../../components/Table";
import TablePagination from "../../../components/Table/Pagination";
import moment from "moment";

const AUDIT_QUICK_FILTERS = [
  { label: "Investment Hub", value: "investment." },
  { label: "Investor KYC", value: "investor.kyc." },
  { label: "Payments", value: "investment.payment." },
  { label: "Capabilities", value: "investor_capabilities." },
  { label: "Investor portal", value: "investor_portal." },
  { label: "Staff & roles", value: "staff." },
  { label: "Role templates", value: "settings.role." },
];

function AuditQuickFilterChip({ label, value, isActive, onSelect }) {
  return (
    <button
      type="button"
      aria-pressed={isActive}
      className={`btn btn-sm rounded-pill px-3 py-2 fw-semibold ${
        isActive ? "border-0 text-white shadow-sm" : "bg-white"
      }`}
      style={
        isActive
          ? {
              backgroundColor: "var(--color-secondary)",
              color: "#ffffff",
            }
          : {
              border: "1px solid var(--color-secondary)",
              color: "var(--color-secondary-readable)",
              backgroundColor: "#ffffff",
            }
      }
      onClick={onSelect}
    >
      {label}
    </button>
  );
}

export default function AuditLogPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialAction = searchParams.get("action") || "";
  const [page, setPage] = useState(1);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [action, setAction] = useState(initialAction);
  const [applied, setApplied] = useState({
    from: "",
    to: "",
    action: initialAction,
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const { data: actionOptions = [] } = useGetAuditLogActionsQuery();

  const queryArgs = useMemo(
    () => ({
      page,
      per_page: 20,
      ...(applied.from && { from_date: applied.from }),
      ...(applied.to && { to_date: applied.to }),
      ...(applied.action && { action: applied.action }),
    }),
    [page, applied]
  );

  const { data, isFetching, error } = useGetAuditLogsQuery(queryArgs);

  const pagination = data?.pagination ?? {};

  const tableData = useMemo(() => {
    const rows = data?.rows ?? [];
    return rows.map((r, idx) => ({
      ...r,
      serialNumber: idx + 1 + (page - 1) * (pagination.perPage || 20),
    }));
  }, [data?.rows, page, pagination.perPage]);

  const columns = useMemo(
    () => [
      {
        name: "S/N",
        width: "72px",
        selector: (row) => row.serialNumber,
      },
      {
        name: "Date",
        minWidth: "160px",
        selector: (row) =>
          row?.created_at
            ? moment(row.created_at).format("DD-MMM-YYYY HH:mm")
            : "—",
      },
      {
        name: "Event",
        minWidth: "180px",
        cell: (row) => (
          <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            <div className="fw-medium text-black1">
              {row?.action_label || row?.action || "—"}
            </div>
            {row?.action && row?.action_label && row.action !== row.action_label ? (
              <div className="small text-muted">{row.action}</div>
            ) : null}
          </div>
        ),
      },
      {
        name: "Actor",
        minWidth: "140px",
        selector: (row) => row?.actor_label || row?.actor_type || "—",
      },
      {
        name: "Details",
        minWidth: "220px",
        grow: 1,
        cell: (row) => (
          <span
            className="small text-muted d-block"
            style={{ whiteSpace: "normal", wordBreak: "break-word" }}
            title={getAuditRowSummary(row)}
          >
            {getAuditRowSummary(row)}
          </span>
        ),
      },
      {
        name: "View",
        width: "100px",
        center: true,
        cell: (row) => (
          <ButtonComp
            btnText="View"
            btnClassName="rounded-pill px-3 py-1 border-0"
            ButtonInlineStyle={{
              fontSize: "12px",
              minWidth: "64px",
              whiteSpace: "nowrap",
              backgroundColor: "var(--color-secondary)",
              color: "#ffffff",
              border: "none",
            }}
            onClick={() => {
              setSelectedRow(row);
              setDetailOpen(true);
            }}
          />
        ),
      },
    ],
    []
  );

  const applyFilters = () => {
    setPage(1);
    setApplied({
      from: fromDate,
      to: toDate,
      action,
    });
  };

  const clearFilters = () => {
    setFromDate("");
    setToDate("");
    setAction("");
    setApplied({ from: "", to: "", action: "" });
    setPage(1);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedRow(null);
  };

  if (error?.status === 401) {
    navigate("/login");
  }

  return (
    <section className="px-2 px-lg-4 min-vh-100 pb-5">
      <CardComp cardClassName="border-0 pt-4 mb-3">
        <h4 className="text-black1 fw-bold mb-0">Audit log</h4>
      </CardComp>

      <CardComp cardClassName="border-0 mb-3" data-tour="staff-audit-filters">
        <div className="d-flex flex-wrap gap-2 mb-3">
          {AUDIT_QUICK_FILTERS.map((chip) => {
            const isActive = applied.action === chip.value;
            return (
              <AuditQuickFilterChip
                key={chip.value}
                label={chip.label}
                value={chip.value}
                isActive={isActive}
                onSelect={() => {
                  const nextAction = isActive ? "" : chip.value;
                  setAction(nextAction);
                  setPage(1);
                  setApplied((prev) => ({ ...prev, action: nextAction }));
                }}
              />
            );
          })}
        </div>
        <div className="row g-3 align-items-end">
          <div className="col-md-3 col-6">
            <label className="form-label small fw-semibold text-black2 mb-1">
              From date
            </label>
            <input
              type="date"
              className="form-control"
              style={{ backgroundColor: "#fafafa" }}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="col-md-3 col-6">
            <label className="form-label small fw-semibold text-black2 mb-1">
              To date
            </label>
            <input
              type="date"
              className="form-control"
              style={{ backgroundColor: "#fafafa" }}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <div className="col-md-4 col-12">
            <label className="form-label small fw-semibold text-black2 mb-1">
              Action
            </label>
            <select
              className="form-select"
              style={{ backgroundColor: "#fafafa" }}
              value={action}
              onChange={(e) => setAction(e.target.value)}
            >
              <option value="">All actions</option>
              {actionOptions.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-2 col-12 d-flex gap-2">
            <ButtonComp
              btnText="Apply"
              btnClassName="bg-new-primary text-white px-4 py-2 rounded border-0 flex-grow-1"
              onClick={applyFilters}
            />
            <ButtonComp
              btnText="Clear"
              btnClassName="border border-primary1 text-primary1 px-3 py-2 rounded bg-white"
              onClick={clearFilters}
            />
          </div>
        </div>
      </CardComp>

      <CardComp cardClassName="border-0 py-0 px-0" data-tour="staff-audit-table">
        <TableCompData
          columns={columns}
          data={tableData}
          progressPending={isFetching}
          progressComponent={<TableSkeletonLoader rows={10} columns={6} />}
          message={
            !isFetching && tableData.length === 0 ? (
              <EmptyTable Message="No audit entries for these filters." />
            ) : null
          }
        />
        {tableData.length > 0 && (
          <TablePagination
            total={pagination.total}
            currentPage={pagination.currentPage}
            totalPages={pagination.lastPage}
            perPage={pagination.perPage}
            count={tableData.length}
            handlePageChange={setPage}
          />
        )}
      </CardComp>

      <ModalCompAdvance
        show={detailOpen}
        handleClose={closeDetail}
        size="lg"
        centered
        close
        designClass="modal-body-scrollable pb-2"
        arrayComp={[
          {
            name: selectedRow?.action_label || "Audit entry",
            component: <AuditEntryDetailModal row={selectedRow} />,
          },
        ]}
        modalBody={0}
      />
    </section>
  );
}
